import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { t } from '../utils/translations';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import ThemeToggle from './ThemeToggle';
import api from '../services/api';
import { createSlugWithId } from '../utils/slugUtils';
import { 
  RectangleStackIcon,
  UserIcon,
  BookOpenIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  HomeIcon,
  ChartBarIcon,
  PuzzlePieceIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

type HomeSearchResult = {
  id: string;
  title: string;
  link: string;
  type: 'Flashcard' | 'Quiz' | 'Match' | 'Study';
};

const Layout: React.FC = () => {
  const { logout, isAdmin, user } = useAuth();
  const { subscription } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomeSearchRoute = ['/home', '/decks', '/quizzes', '/matches', '/studies', '/study'].some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [homeSearchValue, setHomeSearchValue] = useState('');
  const [homeSearchResults, setHomeSearchResults] = useState<HomeSearchResult[]>([]);
  const isAndroidWebView = isUpmindyAndroidWebView();

  // Function to save sidebar state to localStorage
  const saveSidebarState = (isOpen: boolean) => {
    try {
      localStorage.setItem('sidebarOpen', JSON.stringify(isOpen));
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error);
    }
  };

  // Function to load sidebar state from localStorage
  const loadSidebarState = (): boolean | null => {
    try {
      const saved = localStorage.getItem('sidebarOpen');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Failed to load sidebar state from localStorage:', error);
      return null;
    }
  };

  const handleHomeSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedValue = homeSearchValue.trim();
    setHomeSearchResults([]);

    if (!trimmedValue) {
      navigate('/search');
      return;
    }

    navigate(`/search?q=${encodeURIComponent(trimmedValue)}`);
  };

  const handleHomeSearchResultSelect = (link: string) => {
    setHomeSearchResults([]);
    setHomeSearchValue('');
    navigate(link);
  };

  const getHomeSearchResultTheme = (type: HomeSearchResult['type']) => {
    switch (type) {
      case 'Flashcard':
        return {
          icon: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
          text: 'text-blue-700 dark:text-blue-300',
          label: 'text-blue-700/80 dark:text-blue-300/80',
        };
      case 'Quiz':
        return {
          icon: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
          text: 'text-purple-700 dark:text-purple-300',
          label: 'text-purple-700/80 dark:text-purple-300/80',
        };
      case 'Match':
        return {
          icon: 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300',
          text: 'text-pink-700 dark:text-pink-300',
          label: 'text-pink-700/80 dark:text-pink-300/80',
        };
      case 'Study':
        return {
          icon: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',
          text: 'text-teal-700 dark:text-teal-300',
          label: 'text-teal-700/80 dark:text-teal-300/80',
        };
      default:
        return {
          icon: 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300',
          text: 'text-gray-700 dark:text-gray-300',
          label: 'text-gray-700/80 dark:text-gray-300/80',
        };
    }
  };

  useEffect(() => {
    const trimmedValue = homeSearchValue.trim();

    if (!isHomeSearchRoute || !trimmedValue) {
      setHomeSearchResults([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const [publicResponse, studiesResponse] = await Promise.all([
          api.get('/public/search', {
            params: {
              q: trimmedValue,
              type: 'all',
              page: 1,
              limit: 5,
            }
          }),
          api.get('/studies-control', {
            params: {
              search: trimmedValue,
              page: 1,
              limit: 5,
            }
          })
        ]);

        const mappedResults = [
          ...(publicResponse.data?.content || []).map((item: any): HomeSearchResult | null => {
            if (item.contentType === 'deck' || item.name) {
              const resourceTitle = item.name || item.title || 'Flashcard';
              return {
                id: item._id,
                title: resourceTitle,
                link: `/flashcards/${createSlugWithId(resourceTitle, item._id)}`,
                type: 'Flashcard' as const,
              };
            }

            if (item.contentType === 'quiz' || item.title) {
              const resourceTitle = item.title || 'Quiz';
              return {
                id: item._id,
                title: resourceTitle,
                link: `/quizzes/${createSlugWithId(resourceTitle, item._id)}`,
                type: 'Quiz' as const,
              };
            }

            if (item.contentType === 'match' || item.title) {
              const resourceTitle = item.title || 'Match';
              return {
                id: item._id,
                title: resourceTitle,
                link: `/matches/${createSlugWithId(resourceTitle, item._id)}`,
                type: 'Match' as const,
              };
            }

            return null;
          }),
          ...(studiesResponse.data?.studies || []).map((study: any): HomeSearchResult | null => {
            if (!study?._id) {
              return null;
            }

            const resourceTitle = study.title || study.subject || 'Estudo';
            return {
              id: study._id,
              title: resourceTitle,
              link: `/studies/${study._id}`,
              type: 'Study' as const,
            };
          })
        ].filter((item): item is HomeSearchResult => Boolean(item));

        setHomeSearchResults(mappedResults);
      } catch {
        setHomeSearchResults([]);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [homeSearchValue, isHomeSearchRoute]);

  // Set sidebar state with preference for saved state, fallback to responsive behavior
  useEffect(() => {
    const savedState = loadSidebarState();
    
    const handleResize = () => {
      // On mobile (< 1024px), always default to closed unless explicitly opened by user
      // On desktop (>= 1024px), respect saved state or default to open
      if (window.innerWidth < 1024) {
        // Mobile: ignore saved state and always start closed
        setSidebarOpen(false);
      } else {
        // Desktop: respect saved state or default to open
        if (savedState !== null) {
          setSidebarOpen(savedState);
        } else {
          setSidebarOpen(true);
        }
      }
    };

    // Set initial state
    handleResize();

    // Listen for window resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // External links
  const externalLinks: Array<{ name: string; href: string; icon: any; color: 'blue' | 'purple' | 'red' | 'green' | 'pink' }> = [
    { name: t('navigation.documentation'), href: '/docs', icon: BookOpenIcon, color: 'green' },
  ];

  // Mobile navigation items (without Admin for mobile)
  const mobileNavigation = [
    { name: t('navigation.home'), href: '/home', icon: HomeIcon },
    { name: t('navigation.decks'), href: '/decks', icon: RectangleStackIcon },
    { name: t('navigation.quizzes'), href: '/quizzes', icon: PuzzlePieceIcon },
    { name: t('navigation.match'), href: '/matches', icon: SparklesIcon },
    { name: 'Studies', href: '/studies', icon: AcademicCapIcon, badge: 'NOVO' },
  ];

  // Add external links to mobile navigation
  const mobileWithExternalLinks = [...mobileNavigation, ...externalLinks];

  // Desktop navigation items (includes Admin for desktop)
  const desktopNavigation: Array<{ name: string; href: string; icon: any; color: 'blue' | 'purple' | 'red' | 'green' | 'pink' | 'orange' | 'teal'; badge?: string }> = [
    { name: t('navigation.home'), href: '/home', icon: HomeIcon, color: 'green' },
    { name: t('navigation.decks'), href: '/decks', icon: RectangleStackIcon, color: 'blue' },
    { name: t('navigation.quizzes'), href: '/quizzes', icon: PuzzlePieceIcon, color: 'purple' },
    { name: t('navigation.match'), href: '/matches', icon: SparklesIcon, color: 'pink' },
    { name: 'Studies', href: '/studies', icon: AcademicCapIcon, color: 'teal', badge: 'NOVO' },
  ];

  // Add admin link only for desktop if user is admin
  if (isAdmin) {
    desktopNavigation.push({ name: 'Admin', href: '/admin', icon: ShieldCheckIcon, color: 'red' });
  }

  const isActive = (path: string) => location.pathname === path;
  
  // Check if current route is an active study session or quiz results page
  // Hide navbar/circle for: /study/:deckId, /study with Quick Study state, or /quiz/:id/results
  const isActiveStudySession = 
    location.pathname.startsWith('/study/') || 
    (location.pathname === '/study' && location.state?.isQuickStudy) ||
    /^\/quiz\/[^/]+\/results$/.test(location.pathname);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && !isActiveStudySession && (
        <div className="fixed inset-0 flex z-50 lg:hidden">
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75" 
            onClick={() => {
              setSidebarOpen(false);
              saveSidebarState(false);
            }}
          ></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-850 dark:to-gray-900 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 shadow-2xl">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => {
                  setSidebarOpen(false);
                  saveSidebarState(false);
                }}
              >
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
              {/* Mobile Sidebar Content */}
            <div className="flex flex-col h-0 flex-1 pt-6 pb-4 overflow-y-auto">
              <div className="flex items-center justify-between flex-shrink-0 px-6 mb-8">
                <div className="flex items-center space-x-3">
                  <div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">Upmindy</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">LEARNING PLATAFORM</p>
                  </div>
                </div>
                <ThemeToggle />
              </div>
              <nav className="flex-1 px-4 space-y-2">
                {desktopNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const colorClasses: Record<'blue' | 'purple' | 'red' | 'green' | 'pink' | 'orange' | 'teal', string> = {
                    blue: active ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 dark:from-blue-400/20 dark:to-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-400/30' : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300',
                    purple: active ? 'bg-gradient-to-r from-purple-500/10 to-purple-600/10 dark:from-purple-400/20 dark:to-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-400/30' : 'hover:bg-purple-50/50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300',
                    red: active ? 'bg-gradient-to-r from-red-500/10 to-red-600/10 dark:from-red-400/20 dark:to-red-500/20 text-red-700 dark:text-red-300 border-red-200/50 dark:border-red-400/30' : 'hover:bg-red-50/50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300',
                    green: active ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 dark:from-green-400/20 dark:to-green-500/20 text-green-700 dark:text-green-300 border-green-200/50 dark:border-green-400/30' : 'hover:bg-green-50/50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-300',
                    pink: active ? 'bg-gradient-to-r from-pink-500/10 to-pink-600/10 dark:from-pink-400/20 dark:to-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-200/50 dark:border-pink-400/30' : 'hover:bg-pink-50/50 dark:hover:bg-pink-900/20 hover:text-pink-700 dark:hover:text-pink-300',
                    orange: active ? 'bg-gradient-to-r from-orange-500/10 to-orange-600/10 dark:from-orange-400/20 dark:to-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200/50 dark:border-orange-400/30' : 'hover:bg-orange-50/50 dark:hover:bg-orange-900/20 hover:text-orange-700 dark:hover:text-orange-300',
                    teal: active ? 'bg-gradient-to-r from-teal-500/10 to-teal-600/10 dark:from-teal-400/20 dark:to-teal-500/20 text-teal-700 dark:text-teal-300 border-teal-200/50 dark:border-teal-400/30' : 'hover:bg-teal-50/50 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300',
                  };
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => {
                        setSidebarOpen(false);
                        saveSidebarState(false);
                      }}
                      className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 border ${
                        active
                          ? `${colorClasses[item.color]} shadow-sm scale-[1.02]`
                          : `text-gray-600 dark:text-gray-300 border-transparent ${colorClasses[item.color]} hover:scale-[1.01] hover:shadow-sm`
                      }`}
                    >
                      <div className={`mr-4 p-2 rounded-lg transition-colors ${
                        active 
                          ? item.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                            item.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                            item.color === 'red' ? 'bg-red-100 dark:bg-red-900/30' :
                            item.color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30' : 
                            item.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' : 
                            item.color === 'teal' ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-green-100 dark:bg-green-900/30'
                          : item.color === 'blue' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30' :
                            item.color === 'purple' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30' :
                            item.color === 'red' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-red-100 dark:group-hover:bg-red-900/30' :
                            item.color === 'pink' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-pink-100 dark:group-hover:bg-pink-900/30' : 
                            item.color === 'orange' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30' : 
                            item.color === 'teal' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/30' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-green-100 dark:group-hover:bg-green-900/30'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          {item.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-teal-500/20 text-teal-600 dark:text-teal-400 rounded uppercase">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        {active && (
                          <div className="text-xs opacity-70 mt-0.5">Ativo</div>
                        )}
                      </div>
                      {active && (
                        <div className={`w-2 h-2 rounded-full ${
                          item.color === 'blue' ? 'bg-blue-500' :
                          item.color === 'purple' ? 'bg-purple-500' :
                          item.color === 'red' ? 'bg-red-500' :
                          item.color === 'pink' ? 'bg-pink-500' : 
                          item.color === 'orange' ? 'bg-orange-500' : 
                          item.color === 'teal' ? 'bg-teal-500' : 'bg-green-500'
                        }`} />
                      )}
                    </Link>
                  );
                })}
                
                <div className="mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  <div className="px-4 mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recursos</h3>
                  </div>
                  {externalLinks.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    const colorClasses: Record<'blue' | 'purple' | 'red' | 'green' | 'pink', string> = {
                      blue: active ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 dark:from-blue-400/20 dark:to-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-400/30' : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300',
                      purple: active ? 'bg-gradient-to-r from-purple-500/10 to-purple-600/10 dark:from-purple-400/20 dark:to-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-400/30' : 'hover:bg-purple-50/50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300',
                      red: active ? 'bg-gradient-to-r from-red-500/10 to-red-600/10 dark:from-red-400/20 dark:to-red-500/20 text-red-700 dark:text-red-300 border-red-200/50 dark:border-red-400/30' : 'hover:bg-red-50/50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300',
                      green: active ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 dark:from-green-400/20 dark:to-green-500/20 text-green-700 dark:text-green-300 border-green-200/50 dark:border-green-400/30' : 'hover:bg-green-50/50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-300',
    pink: active ? 'bg-gradient-to-r from-pink-500/10 to-pink-600/10 dark:from-pink-400/20 dark:to-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-200/50 dark:border-pink-400/30' : 'hover:bg-pink-50/50 dark:hover:bg-pink-900/20 hover:text-pink-700 dark:hover:text-pink-300',
                    };
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => {
                          setSidebarOpen(false);
                          saveSidebarState(false);
                        }}
                        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 border mb-2 ${
                          active
                            ? `${colorClasses[item.color]} shadow-sm scale-[1.02]`
                            : `text-gray-600 dark:text-gray-300 border-transparent ${colorClasses[item.color]} hover:scale-[1.01] hover:shadow-sm`
                        }`}
                      >
                        <div className={`mr-4 p-2 rounded-lg transition-colors ${
                          active 
                            ? item.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                            : item.color === 'green' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-green-100 dark:group-hover:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{item.name}</div>
                          {active && (
                            <div className="text-xs opacity-70 mt-0.5">Ativo</div>
                          )}
                        </div>
                        {active && (
                          <div className={`w-2 h-2 rounded-full ${
                            item.color === 'green' ? 'bg-green-500' : 'bg-blue-500'
                          }`} />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </nav>
              
              {/* Legal Links - Mobile */}
              <div className="px-4 pb-4 space-y-1">
                <div className="flex flex-col space-y-1">
                  <Link 
                    to="/terms-of-service" 
                    onClick={() => {
                      setSidebarOpen(false);
                      saveSidebarState(false);
                    }}
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Termos de Uso
                  </Link>
                  <Link 
                    to="/privacy-policy" 
                    onClick={() => {
                      setSidebarOpen(false);
                      saveSidebarState(false);
                    }}
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Política de Privacidade
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Mobile Sidebar Footer */}
            <div className="flex-shrink-0 border-t border-gray-200/50 dark:border-gray-700/50 p-4 space-y-3">
              {/* User Profile Card */}
              {user?.email && (
                <Link
                  to="/profile"
                  onClick={() => {
                    setSidebarOpen(false);
                    saveSidebarState(false);
                  }}
                  className="block bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-3 border border-gray-200/30 dark:border-gray-700/30 hover:bg-white/70 dark:hover:bg-gray-800/70 transition-all duration-200 hover:scale-[1.02]"
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user.email.split('@')[0]}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </Link>
              )}
              
              {/* Upgrade Button - Only for free users and not in Android WebView */}
              {subscription?.subscriptionTier === 'free' && !isAndroidWebView && (
                <Link
                  to="/pricing"
                  onClick={() => {
                    setSidebarOpen(false);
                    saveSidebarState(false);
                  }}
                  className="group flex items-center w-full px-4 py-3 text-sm font-medium bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 text-purple-700 dark:text-purple-300 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-900/30 dark:hover:to-pink-900/30 rounded-xl border border-purple-200/50 dark:border-purple-400/20 transition-all duration-200 hover:shadow-sm hover:scale-[1.02]"
                >
                  <div className="mr-3 p-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                    <SparklesIcon className="h-4 w-4" />
                  </div>
                  <span>Seja Premium</span>
                </Link>
              )}
              
              {/* Logout button */}
              <button
                onClick={logout}
                className="group flex items-center w-full px-4 py-3 text-sm font-medium bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 text-red-700 dark:text-red-300 hover:from-red-100 hover:to-pink-100 dark:hover:from-red-900/30 dark:hover:to-pink-900/30 rounded-xl border border-red-200/50 dark:border-red-400/20 transition-all duration-200 hover:shadow-sm hover:scale-[1.02]"
              >
                <div className="mr-3 p-1.5 bg-red-100 dark:bg-red-900/40 rounded-lg">
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </div>
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      {!isActiveStudySession && (
        <div className={`hidden lg:flex lg:flex-shrink-0 lg:fixed lg:left-0 lg:top-0 lg:bottom-0 lg:z-10 transition-all duration-300 ${sidebarOpen ? '' : 'lg:-ml-64'}`}>
        <div className="flex flex-col w-64 h-full">
          <div className="flex flex-col h-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-850 dark:to-gray-900 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 shadow-2xl">
            <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
              <div className="flex items-center justify-between flex-shrink-0 px-6 mb-8">
                <div className="flex items-center space-x-3">
                  <div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">Upmindy</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">LEARNING PLATAFORM</p>
                  </div>
                </div>
                <ThemeToggle />
              </div>
              <nav className="flex-1 px-4 space-y-2">
                {desktopNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const colorClasses: Record<'blue' | 'purple' | 'red' | 'green' | 'pink' | 'orange' | 'teal', string> = {
                    blue: active ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 dark:from-blue-400/20 dark:to-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-400/30' : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300',
                    purple: active ? 'bg-gradient-to-r from-purple-500/10 to-purple-600/10 dark:from-purple-400/20 dark:to-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-400/30' : 'hover:bg-purple-50/50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300',
                    red: active ? 'bg-gradient-to-r from-red-500/10 to-red-600/10 dark:from-red-400/20 dark:to-red-500/20 text-red-700 dark:text-red-300 border-red-200/50 dark:border-red-400/30' : 'hover:bg-red-50/50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300',
                    green: active ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 dark:from-green-400/20 dark:to-green-500/20 text-green-700 dark:text-green-300 border-green-200/50 dark:border-green-400/30' : 'hover:bg-green-50/50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-300',
                    pink: active ? 'bg-gradient-to-r from-pink-500/10 to-pink-600/10 dark:from-pink-400/20 dark:to-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-200/50 dark:border-pink-400/30' : 'hover:bg-pink-50/50 dark:hover:bg-pink-900/20 hover:text-pink-700 dark:hover:text-pink-300',
                    orange: active ? 'bg-gradient-to-r from-orange-500/10 to-orange-600/10 dark:from-orange-400/20 dark:to-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200/50 dark:border-orange-400/30' : 'hover:bg-orange-50/50 dark:hover:bg-orange-900/20 hover:text-orange-700 dark:hover:text-orange-300',
                    teal: active ? 'bg-gradient-to-r from-teal-500/10 to-teal-600/10 dark:from-teal-400/20 dark:to-teal-500/20 text-teal-700 dark:text-teal-300 border-teal-200/50 dark:border-teal-400/30' : 'hover:bg-teal-50/50 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300',
                  };
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 border ${
                        active
                          ? `${colorClasses[item.color]} shadow-sm scale-[1.02]`
                          : `text-gray-600 dark:text-gray-300 border-transparent ${colorClasses[item.color]} hover:scale-[1.01] hover:shadow-sm`
                      }`}
                    >
                      <div className={`mr-4 p-2 rounded-lg transition-colors ${
                        active 
                          ? item.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                            item.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                            item.color === 'red' ? 'bg-red-100 dark:bg-red-900/30' :
                            item.color === 'pink' ? 'bg-pink-100 dark:bg-pink-900/30' : 
                            item.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' : 
                            item.color === 'teal' ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-green-100 dark:bg-green-900/30'
                          : item.color === 'blue' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30' :
                            item.color === 'purple' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30' :
                            item.color === 'red' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-red-100 dark:group-hover:bg-red-900/30' :
                            item.color === 'pink' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-pink-100 dark:group-hover:bg-pink-900/30' : 
                            item.color === 'orange' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30' : 
                            item.color === 'teal' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/30' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-green-100 dark:group-hover:bg-green-900/30'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          {item.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-teal-500/20 text-teal-600 dark:text-teal-400 rounded uppercase">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        {active && (
                          <div className="text-xs opacity-70 mt-0.5">Ativo</div>
                        )}
                      </div>
                      {active && (
                        <div className={`w-2 h-2 rounded-full ${
                          item.color === 'blue' ? 'bg-blue-500' :
                          item.color === 'purple' ? 'bg-purple-500' :
                          item.color === 'red' ? 'bg-red-500' :
                          item.color === 'pink' ? 'bg-pink-500' : 
                          item.color === 'orange' ? 'bg-orange-500' : 
                          item.color === 'teal' ? 'bg-teal-500' : 'bg-green-500'
                        }`} />
                      )}
                    </Link>
                  );
                })}
                
                <div className="mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  <div className="px-4 mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recursos</h3>
                  </div>
                  {externalLinks.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    const colorClasses: Record<'blue' | 'purple' | 'red' | 'green' | 'pink', string> = {
                      blue: active ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 dark:from-blue-400/20 dark:to-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-400/30' : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300',
                      purple: active ? 'bg-gradient-to-r from-purple-500/10 to-purple-600/10 dark:from-purple-400/20 dark:to-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-400/30' : 'hover:bg-purple-50/50 dark:hover:bg-purple-900/20 hover:text-purple-700 dark:hover:text-purple-300',
                      red: active ? 'bg-gradient-to-r from-red-500/10 to-red-600/10 dark:from-red-400/20 dark:to-red-500/20 text-red-700 dark:text-red-300 border-red-200/50 dark:border-red-400/30' : 'hover:bg-red-50/50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300',
                      green: active ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 dark:from-green-400/20 dark:to-green-500/20 text-green-700 dark:text-green-300 border-green-200/50 dark:border-green-400/30' : 'hover:bg-green-50/50 dark:hover:bg-green-900/20 hover:text-green-700 dark:hover:text-green-300',
    pink: active ? 'bg-gradient-to-r from-pink-500/10 to-pink-600/10 dark:from-pink-400/20 dark:to-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-200/50 dark:border-pink-400/30' : 'hover:bg-pink-50/50 dark:hover:bg-pink-900/20 hover:text-pink-700 dark:hover:text-pink-300',
                    };
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 border mb-2 ${
                          active
                            ? `${colorClasses[item.color]} shadow-sm scale-[1.02]`
                            : `text-gray-600 dark:text-gray-300 border-transparent ${colorClasses[item.color]} hover:scale-[1.01] hover:shadow-sm`
                        }`}
                      >
                        <div className={`mr-4 p-2 rounded-lg transition-colors ${
                          active 
                            ? item.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                            : item.color === 'green' ? 'bg-gray-100 dark:bg-gray-700 group-hover:bg-green-100 dark:group-hover:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{item.name}</div>
                          {active && (
                            <div className="text-xs opacity-70 mt-0.5">Ativo</div>
                          )}
                        </div>
                        {active && (
                          <div className={`w-2 h-2 rounded-full ${
                            item.color === 'green' ? 'bg-green-500' : 'bg-blue-500'
                          }`} />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </nav>
              
              {/* Legal Links - Desktop */}
              <div className="px-4 pb-4 space-y-1">
                <div className="flex flex-col space-y-1">
                  <Link 
                    to="/terms-of-service" 
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Termos de Uso
                  </Link>
                  <Link 
                    to="/privacy-policy" 
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Política de Privacidade
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Desktop Sidebar Footer */}
            <div className="flex-shrink-0 border-t border-gray-200/50 dark:border-gray-700/50 p-4 space-y-3">
              {/* User Profile Card */}
              {user?.email && (
                <Link
                  to="/profile"
                  className="block bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-3 border border-gray-200/30 dark:border-gray-700/30 hover:bg-white/70 dark:hover:bg-gray-800/70 transition-all duration-200 hover:scale-[1.02]"
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user.email.split('@')[0]}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </Link>
              )}
              
              {/* Upgrade Button - Only for free users and not in Android WebView */}
              {subscription?.subscriptionTier === 'free' && !isAndroidWebView && (
                <Link
                  to="/pricing"
                  className="group flex items-center w-full px-4 py-3 text-sm font-medium bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 text-purple-700 dark:text-purple-300 hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-900/30 dark:hover:to-pink-900/30 rounded-xl border border-purple-200/50 dark:border-purple-400/20 transition-all duration-200 hover:shadow-sm hover:scale-[1.02]"
                >
                  <div className="mr-3 p-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                    <SparklesIcon className="h-4 w-4" />
                  </div>
                  <span>Seja Premium</span>
                </Link>
              )}
              
              {/* Logout button */}
              <button
                onClick={logout}
                className="group flex items-center w-full px-4 py-3 text-sm font-medium bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 text-red-700 dark:text-red-300 hover:from-red-100 hover:to-pink-100 dark:hover:from-red-900/30 dark:hover:to-pink-900/30 rounded-xl border border-red-200/50 dark:border-red-400/20 transition-all duration-200 hover:shadow-sm hover:scale-[1.02]"
              >
                <div className="mr-3 p-1.5 bg-red-100 dark:bg-red-900/40 rounded-lg">
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </div>
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Main content area */}
      <div className={`flex flex-col flex-1 min-w-0 transition-all duration-300 ${!isActiveStudySession && sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
        {/* Top bar with hamburger button */}
        {!isActiveStudySession && (
          <>
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 px-4 py-3 shadow-sm sticky top-0 z-30 overflow-visible">
              <div className="flex items-center h-15 relative z-10">
                <div className="flex items-center gap-4 flex-1 justify-start">
                  <button
                    onClick={() => {
                      const newState = !sidebarOpen;
                      setSidebarOpen(newState);
                      saveSidebarState(newState);
                    }}
                    className="inline-flex items-center justify-center p-2.5 rounded-xl text-gray-400 hover:text-gray-500 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-all duration-200 hover:scale-105"
                  >
                    <span className="sr-only">Toggle sidebar</span>
                    <Bars3Icon className="h-6 w-6" />
                  </button>

                  {isHomeSearchRoute && (
                    <div className="hidden md:flex z-10">
                      <form onSubmit={handleHomeSearchSubmit} className="relative z-50 w-64 lg:w-80">
                        <label htmlFor="home-navbar-search" className="sr-only">Pesquisar conteúdo</label>
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-3 pr-3 shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                          <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                          <input
                            id="home-navbar-search"
                            type="text"
                            value={homeSearchValue}
                            onChange={(event) => setHomeSearchValue(event.target.value)}
                            placeholder="Pesquisar conteúdo..."
                            className="w-full border-0 bg-transparent text-sm text-gray-900 placeholder-gray-500 outline-none dark:text-white dark:placeholder-gray-400"
                          />
                        </div>

                        {homeSearchValue.trim() && homeSearchResults.length > 0 && (
                          <div className="absolute left-0 right-0 top-full z-[60] mt-2 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
                            {homeSearchResults.map((result) => {
                              const theme = getHomeSearchResultTheme(result.type);

                              return (
                                <button
                                  key={`${result.type}-${result.id}`}
                                  type="button"
                                  onClick={() => handleHomeSearchResultSelect(result.link)}
                                  className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  <span className={`flex h-7 w-7 items-center justify-center rounded-md ${theme.icon}`}>
                                    <MagnifyingGlassIcon className="h-4 w-4" />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className={`block text-[10px] font-semibold uppercase tracking-[0.12em] ${theme.label}`}>
                                      {result.type}
                                    </span>
                                    <span className={`block truncate text-sm font-medium ${theme.text}`}>{result.title}</span>
                                  </span>
                                </button>
                              );
                            })}
                            <Link
                              to={`/search${homeSearchValue.trim() ? `?q=${encodeURIComponent(homeSearchValue.trim())}` : ''}`}
                              onClick={() => {
                                setHomeSearchResults([]);
                                setHomeSearchValue('');
                              }}
                              className="block border-t border-gray-200 px-3 py-2 text-center text-sm font-medium text-blue-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-blue-300 dark:hover:bg-gray-700"
                            >
                              veja mais
                            </Link>
                          </div>
                        )}
                      </form>
                    </div>
                  )}
                </div>

                {!isAndroidWebView && (
                  <div className="flex justify-center min-w-[180px]">
                    {subscription?.subscriptionTier === 'free' ? (
                    <Link 
                      to="/pricing"
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                    >
                      <SparklesIcon className="h-5 w-5" />
                      <span className="text-sm font-semibold">Seja Premium</span>
                    </Link>
                    ) : <div 
                          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                        >
                          <SparklesIcon className="h-5 w-5" />
                          <span className="text-sm font-semibold">Premium</span>
                        </div>}
                  </div>
                )}
                {isAndroidWebView && (
                  <div className="flex-1" />
                )}
                {!isAndroidWebView && <div className="flex-1" />}
              </div>
            </div>

            {isHomeSearchRoute && (
              <div className="md:hidden border-t border-gray-200 bg-white/95 px-4 py-2 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/95 overflow-visible relative z-10">
                <form onSubmit={handleHomeSearchSubmit} className="relative z-10">
                  <label htmlFor="home-mobile-navbar-search" className="sr-only">Pesquisar conteúdo</label>
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-3 pr-3 shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                    <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                    <input
                      id="home-mobile-navbar-search"
                      type="text"
                      value={homeSearchValue}
                      onChange={(event) => setHomeSearchValue(event.target.value)}
                      placeholder="Pesquisar conteúdo..."
                      className="w-full border-0 bg-transparent text-sm leading-4 text-gray-900 placeholder-gray-500 outline-none dark:text-white dark:placeholder-gray-400"
                    />
                  </div>

                  {homeSearchValue.trim() && homeSearchResults.length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-[60] mt-2 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
                      {homeSearchResults.map((result) => {
                        const theme = getHomeSearchResultTheme(result.type);

                        return (
                          <button
                            key={`${result.type}-${result.id}`}
                            type="button"
                            onClick={() => handleHomeSearchResultSelect(result.link)}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <span className={`flex h-7 w-7 items-center justify-center rounded-md ${theme.icon}`}>
                              <MagnifyingGlassIcon className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className={`block text-[10px] font-semibold uppercase tracking-[0.12em] ${theme.label}`}>
                                {result.type}
                              </span>
                              <span className={`block truncate text-sm font-medium ${theme.text}`}>{result.title}</span>
                            </span>
                          </button>
                        );
                      })}
                      <Link
                        to={`/search${homeSearchValue.trim() ? `?q=${encodeURIComponent(homeSearchValue.trim())}` : ''}`}
                        onClick={() => {
                          setHomeSearchResults([]);
                          setHomeSearchValue('');
                        }}
                        className="block border-t border-gray-200 px-3 py-2 text-center text-sm font-medium text-blue-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-blue-300 dark:hover:bg-gray-700"
                      >
                        veja mais
                      </Link>
                    </div>
                  )}
                </form>
              </div>
            )}
          </>
        )}

        {/* Main content */}
        <div className="flex-1">
          <main className="flex-1">
            <div className={`${!isActiveStudySession ? 'p-4 sm:p-6 lg:p-8' : ''}`}>
              <Outlet />
            </div>
          </main>
        </div>

        {/* Footer - Only visible on profile page */}
        {location.pathname === '/profile' && (
          <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                <Link 
                  to="/terms-of-service" 
                  className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Termos de Uso
                </Link>
                <Link 
                  to="/privacy-policy" 
                  className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Política de Privacidade
                </Link>
              </div>
            </div>
          </footer>
        )}
      </div>

    </div>
  );
};

export default Layout;
